import { forwardRef } from 'react'

import { Box, IconButton, Typography } from '@mui/material'
import { CustomContentProps, SnackbarContent, SnackbarProvider, closeSnackbar } from 'notistack'

export const captionStyle = {
  color: '#212020',
  fontFeatureSettings: "'liga' off, 'clig' off",
  fontFamily: 'Roboto',
  fontSize: '12px',
  fontStyle: 'normal',
  fontWeight: 500,
  lineHeight: '14px',
}

export const SuccessIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M12 2C6.5 2 2 6.5 2 12C2 17.5 6.5 22 12 22C17.5 22 22 17.5 22 12C22 6.5 17.5 2 12 2ZM10 17L5 12L6.41 10.59L10 14.17L17.59 6.58L19 8L10 17Z"
      fill="#103767"
    />
  </svg>
)

export const ErrorIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
    <g clipPath="url(#clip0_4250_378)">
      <path
        d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 17H11V15H13V17ZM13 13H11V7H13V13Z"
        fill="#E21616"
      />
    </g>
    <defs>
      <clipPath id="clip0_4250_378">
        <rect width="24" height="24" fill="white" />
      </clipPath>
    </defs>
  </svg>
)

const CloseIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="currentColor"
  >
    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
  </svg>
)

export type SnackbarVariant = 'success' | 'error'

export interface SnackbarVariantConfig {
  backgroundColor: string
  iconColor: string
  boxShadow?: string
  showClose: boolean
  role: 'status' | 'alert'
  Icon: () => JSX.Element
}

export const variantConfig: Record<SnackbarVariant, SnackbarVariantConfig> = {
  success: {
    backgroundColor: '#C4E6F1',
    iconColor: '#103767',
    showClose: true,
    role: 'status',
    Icon: SuccessIcon,
  },
  error: {
    backgroundColor: '#FCF3F5',
    iconColor: '#E21616',
    boxShadow: '0 2px 4px 0 #F1F1F1',
    showClose: false,
    role: 'alert',
    Icon: ErrorIcon,
  },
}

const createSnackbar = (variant: SnackbarVariant) => {
  const { backgroundColor, iconColor, boxShadow, showClose, role, Icon } = variantConfig[variant]

  const Component = forwardRef<HTMLDivElement, CustomContentProps>(({ id, message }, ref) => (
    <SnackbarContent ref={ref} role={role} data-testid={`${variant}-snackbar`}>
      <Box
        sx={{
          display: 'inline-flex',
          padding: '16px 24px 16px 16px',
          alignItems: 'center',
          gap: '104px',
          borderRadius: '4px',
          backgroundColor,
          ...(boxShadow && { boxShadow }),
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Box sx={{ width: 24, height: 24, flexShrink: 0 }}>
            <Icon />
          </Box>
          <Typography sx={captionStyle}>{message}</Typography>
        </Box>
        {showClose && (
          <IconButton
            size="small"
            onClick={() => closeSnackbar(id)}
            aria-label="close"
            sx={{ color: iconColor, padding: 0 }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </Box>
    </SnackbarContent>
  ))

  Component.displayName = `${variant.charAt(0).toUpperCase() + variant.slice(1)}Snackbar`
  return Component
}

export const SuccessSnackbar = createSnackbar('success')
export const ErrorSnackbar = createSnackbar('error')

interface CustomSnackbarProviderProps {
  children: React.ReactNode
}

export const CustomSnackbarProvider = ({ children }: CustomSnackbarProviderProps) => (
  <SnackbarProvider
    maxSnack={3}
    Components={{ error: ErrorSnackbar, success: SuccessSnackbar }}
    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
  >
    {children}
  </SnackbarProvider>
)
